import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Public hook reading which optional homepage widgets are currently
 * visible. Backed by `GET /api/homepage-visibility`. Listens for
 * `homepage-visibility:changed` window events so admin toggles update
 * the homepage without a hard refresh.
 *
 * To prevent a "flash of always-visible" before the API responds we
 * cache the last server response in `localStorage` and seed the state
 * with it. The very first visit still falls back to the safe defaults
 * below.
 *
 * Also exposes a `loaded` flag so components that prefer to wait for
 * a definitive answer (eg. the heart-rate bubble which should stay
 * hidden when the broadcaster has turned it off) can gate on it.
 */
const STORAGE_KEY = 'jdv_homepage_visibility_v1';

const DURATION_MIN_S = 20;
const DURATION_MAX_S = 600;

const DEFAULTS = {
  stats_ticker: true,
  stats_ticker_position: 'bottom',
  stats_ticker_items: null,  // null = show every item
  stats_ticker_duration_s: 90,
  follower_ticker_duration_s: 45,
  hero_bubble: true,
  heart_rate: true,
  heart_rate_only_live: false,
};

function clampDuration(val, fallback) {
  const n = Number(val);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(DURATION_MAX_S, Math.max(DURATION_MIN_S, Math.round(n)));
}

function normalize(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const pos = raw.stats_ticker_position === 'top' ? 'top' : 'bottom';
  const items = raw.stats_ticker_items;
  return {
    stats_ticker: raw.stats_ticker !== false,
    stats_ticker_position: pos,
    stats_ticker_items: Array.isArray(items) ? items.map(String) : null,
    stats_ticker_duration_s: clampDuration(
      raw.stats_ticker_duration_s,
      DEFAULTS.stats_ticker_duration_s,
    ),
    follower_ticker_duration_s: clampDuration(
      raw.follower_ticker_duration_s,
      DEFAULTS.follower_ticker_duration_s,
    ),
    hero_bubble:  raw.hero_bubble  !== false,
    heart_rate:   raw.heart_rate   !== false,
    heart_rate_only_live: raw.heart_rate_only_live === true,
  };
}

function readCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalize(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCache(state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota / private mode */ }
}

export default function useHomepageVisibility() {
  const cached = readCache();
  const [vis, setVis] = useState(cached || DEFAULTS);
  const [loaded, setLoaded] = useState(Boolean(cached));

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/homepage-visibility');
        if (!alive) return;
        const next = normalize(r.data) || DEFAULTS;
        setVis(next);
        setLoaded(true);
        writeCache(next);
      } catch {
        if (alive) setLoaded(true); // still flip so gates open
      }
    };
    load();
    const onChange = (e) => {
      const d = e?.detail;
      if (!d || typeof d !== 'object') return;
      setVis((prev) => {
        const nextPos = d.stats_ticker_position === 'top' || d.stats_ticker_position === 'bottom'
          ? d.stats_ticker_position
          : prev.stats_ticker_position;
        const nextItems = (() => {
          if (!('stats_ticker_items' in d)) return prev.stats_ticker_items;
          const v = d.stats_ticker_items;
          if (v === null) return null;
          return Array.isArray(v) ? v.map(String) : prev.stats_ticker_items;
        })();
        const next = {
          stats_ticker: typeof d.stats_ticker === 'boolean' ? d.stats_ticker : prev.stats_ticker,
          stats_ticker_position: nextPos,
          stats_ticker_items: nextItems,
          stats_ticker_duration_s:
            'stats_ticker_duration_s' in d
              ? clampDuration(d.stats_ticker_duration_s, prev.stats_ticker_duration_s)
              : prev.stats_ticker_duration_s,
          follower_ticker_duration_s:
            'follower_ticker_duration_s' in d
              ? clampDuration(d.follower_ticker_duration_s, prev.follower_ticker_duration_s)
              : prev.follower_ticker_duration_s,
          hero_bubble:  typeof d.hero_bubble  === 'boolean' ? d.hero_bubble  : prev.hero_bubble,
          heart_rate:   typeof d.heart_rate   === 'boolean' ? d.heart_rate   : prev.heart_rate,
          heart_rate_only_live: typeof d.heart_rate_only_live === 'boolean' ? d.heart_rate_only_live : prev.heart_rate_only_live,
        };
        writeCache(next);
        return next;
      });
    };
    window.addEventListener('homepage-visibility:changed', onChange);
    return () => {
      alive = false;
      window.removeEventListener('homepage-visibility:changed', onChange);
    };
  }, []);

  return { ...vis, loaded };
}
